import type {
	ClientCheckGate,
	ClientCheckMode,
	ClientPassBinding,
	EffectiveClientVerificationPolicy,
	RateLimitBudget,
	TurnstileAppearance,
} from "@repo/api-dtos"

type RateLimits = EffectiveClientVerificationPolicy["rateLimits"]

/** The rate-limit policy keys that hold a calls-per-window budget. */
export type BudgetKey = {
	[K in keyof RateLimits]: RateLimits[K] extends RateLimitBudget | null
		? K
		: never
}[keyof RateLimits]

/** Name, help line and tooltip of one setting. */
export interface FieldCopy {
	label: string
	/** One short line under the label: what the setting is. */
	help: string
	/** The tooltip beside the label: what happens when it changes. */
	tooltip: string
}

/** Label of an option and what choosing it does. */
export interface OptionCopy {
	label: string
	effect: string
}

export interface GateCopy extends FieldCopy {
	/** Tooltip of each mode option: what this gate does in that mode. */
	modes: Record<ClientCheckMode, string>
	/** One short sentence per mode for the summary and the change preview. */
	outcome: Record<ClientCheckMode, string>
	/** What this server lacks when the gate is not configured; null when it is always configured. */
	missing: string | null
}

/** Title and description of the page, from the page intro. */
export const PAGE_TITLE = "Client verification"
export const PAGE_DESCRIPTION =
	"Choose how the private API checks where each call comes from: the website, the mobile app, our server-rendered pages, scripts with an internal pass, or admins."

/** The three modes every gate has. */
export const MODE_COPY: Record<
	ClientCheckMode,
	{ label: string; short: string; tooltip: string }
> = {
	off: {
		label: "Off",
		short: "Not checked",
		tooltip:
			"The gate is skipped and nothing is recorded for it. When every gate is off, the API skips client verification completely. The only exception is Pass issuing, per IP, which the pass endpoint always applies.",
	},
	log: {
		label: "Log",
		short: "Measured, never refused",
		tooltip:
			'The gate checks every call and always lets it through. Calls it would refuse are counted in Dash0 under orpc.client_checks with outcome would_block. They are also logged as "Client check would block a call", at most one line every 10 seconds on each server for each gate, reason, caller kind and platform. tampered, binding_mismatch and wrong_env are logged every time. Use Log to measure before you enforce.',
	},
	enforce: {
		label: "Enforce",
		short: "Failing calls refused",
		tooltip:
			'Calls that fail the gate are refused, counted with outcome block and logged as "Client check blocked a call". The origin check answers 403 FORBIDDEN. The web and app gates answer 403 CLIENT_PASS_REQUIRED, and the website and app then get a new proof and retry once. Rate limits answer 429 RATE_LIMITED. WebSocket connections are refused with HTTP 403 or closed with code 4403. Enforce can\'t be chosen while a gate is not configured.',
	},
}

/** Every gate: its row copy, the tooltip of each mode and the outcome of each mode. */
export const GATE_COPY: Record<ClientCheckGate, GateCopy> = {
	origin: {
		label: "Origin check",
		help: "Calls must reach the API through Cloudflare, not straight to our servers.",
		tooltip:
			"Cloudflare adds a secret header (x-cardnexus-origin-auth) to every request it forwards. A browser or app call without that header went around Cloudflare and straight to our load balancer. This gate covers browsers, the app and scripts with an internal pass. It lets through two callers without the header: our server-rendered pages, whose calls from the website Worker never pass Cloudflare's rule but carry our signature, and admins, so this page can always switch the gate back. A refused call gets 403 FORBIDDEN, the same answer as an IP ban, and a refused WebSocket gets HTTP 403 before it opens.",
		modes: {
			off: "Not checked. Nothing is recorded.",
			log: "Calls without the right header are recorded as would_block (reason origin_header_missing or origin_header_mismatch) and still served. Server-rendered calls and admins without the header are recorded as skip, not would_block.",
			enforce:
				"Calls without the right header get 403 FORBIDDEN, except server-rendered calls and admins, and requests that open a WebSocket are refused. Needs CF_ORIGIN_AUTH_SECRET, and saving is refused (ORIGIN_NOT_VERIFIED) unless this page itself reaches the API through Cloudflare. Enforce it once no real client has called without the header for 7 days in a row.",
		},
		outcome: {
			off: "The origin header is not checked.",
			log: "Calls that went around Cloudflare are recorded and still served.",
			enforce:
				"Calls that went around Cloudflare are refused with 403 FORBIDDEN.",
		},
		missing: "CF_ORIGIN_AUTH_SECRET is missing",
	},
	web_pass: {
		label: "Web pass",
		help: "Every call that doesn't come from the app (browsers, but also scripts and bots) must carry a pass earned by solving Cloudflare's challenge.",
		tooltip:
			"The website solves Turnstile, Cloudflare's bot challenge, which most visitors never see. It swaps the result for a signed pass and sends that pass with every call (x-client-pass). This gate judges every HTTP call that isn't identified as the app, so scripts and bots without an internal pass are judged here too. Server-rendered calls, internal passes and admins always pass, and the calls that fetch a pass, read the policy or check for maintenance are exempt. Until Mobile App Check is enforced, a scraper can claim to be the app (x-platform: mobile) and be judged by that gate instead.",
		modes: {
			off: "Not checked. Browsers stop solving the challenge only once WebSocket, web is off too.",
			log: "Browsers solve the challenge in the background and never wait for it. Calls without a valid pass are recorded as would_block (reason missing, expired, binding_mismatch and so on) and still served.",
			enforce:
				"Without a valid pass, the website holds each call for up to 8 seconds while it earns one. A call without a valid pass gets 403 CLIENT_PASS_REQUIRED. The website then drops its pass, solves again and retries the call once. It opens the challenge dialog if Cloudflare asks for a click, and solves again for a rejected pass at most once every 5 minutes. Needs WebSocket, web on Enforce first. Enforce it once cardnexus.com is served by the Cloudflare Worker, sitemap calls show as server-rendered, and calls without a pass are under 0.5% of browser calls from real sessions for 7 days.",
		},
		outcome: {
			off: "Web passes are not checked.",
			log: "Calls not from the app without a valid pass are recorded and still served.",
			enforce:
				"Calls not from the app without a valid pass are refused with 403 CLIENT_PASS_REQUIRED, and the website solves the challenge again and retries once.",
		},
		missing:
			"CLIENT_PASS_SIGNING_SECRET or a real TURNSTILE_SECRET_KEY is missing",
	},
	app_check: {
		label: "Mobile App Check",
		help: "App calls must carry a Firebase App Check token.",
		tooltip:
			"App Check is Firebase's proof that a call comes from our genuine app on a real device. The app sends a fresh token with every call (x-firebase-appcheck). This gate judges HTTP calls that say they come from the app (header x-platform: mobile, sent without a browser user agent or browser Origin) or that carry a valid App Check token. A mobile claim from a browser is judged by Web pass and recorded as platform_claim_mismatch. A pass from the app's challenge fallback also passes, and so do internal passes and admins. If Google's signing keys can't be loaded (none fetched yet, or the last copy is over 6 hours old), the gate lets app calls through. It needs no server secret, so it is always configured.",
		modes: {
			off: "Not checked.",
			log: "App calls without a valid token are recorded as would_block and still served. The reason is missing, invalid_token, expired, or app_not_allowed (a token from another Firebase app). The app never waits for its token.",
			enforce:
				"App calls without a valid token get 403 CLIENT_PASS_REQUIRED, and the app refreshes its token and retries once. App builds without App Check are refused, so first raise min_app_version in Firebase Remote Config (with force_update_enabled on), which makes older app builds ask users to update. Needs WebSocket, mobile on Enforce first. Enforce it about 4 days after the first app build with App Check is in the stores, once min_app_version is raised and few enough app calls arrive without App Check.",
		},
		outcome: {
			off: "App Check tokens are not checked.",
			log: "App calls without a valid token are recorded and still served.",
			enforce:
				"App calls without a valid token are refused with 403 CLIENT_PASS_REQUIRED, and the app refreshes its token and retries once.",
		},
		missing: null,
	},
	ws_web: {
		label: "WebSocket, web",
		help: "Live-update connections that don't come from the app must present a pass when they open.",
		tooltip:
			"The live-update connection (/ws) serves the same procedures as normal calls. A browser presents its pass when the connection opens, as the pass URL parameter. Every message is then judged by that pass, including its expiry, so a connection that outlives its pass fails at its next message. Internal passes and admins pass. A revoked internal pass stops counting within about 30 seconds, and a removed admin role within 60 seconds.",
		modes: {
			off: "Not checked.",
			log: "Connections and messages without a valid pass are recorded and still served. They show in orpc.ws_upgrades, and in orpc.client_checks with would_block.",
			enforce:
				"A connection attempt with no pass at all gets HTTP 403. A connection attempt with an invalid or expired pass opens, then closes with code 4403, and the website earns a new pass and reconnects. A message refused later gets CLIENT_PASS_REQUIRED, and the connection closes with 4403 one second later. Without a pass, the website waits up to 8 seconds before connecting. Enforce this before Web pass, once Web pass itself is ready to enforce.",
		},
		outcome: {
			off: "Live-update connections not from the app are not checked.",
			log: "Connections not from the app, and their messages, without a valid pass are recorded and still served.",
			enforce:
				"Connections not from the app without a valid pass are refused with HTTP 403 or closed with code 4403.",
		},
		missing:
			"CLIENT_PASS_SIGNING_SECRET or a real TURNSTILE_SECRET_KEY is missing",
	},
	ws_mobile: {
		label: "WebSocket, mobile",
		help: "App live-update connections must present an App Check token when they open.",
		tooltip:
			"The app sends its App Check token as a header when it opens the live-update connection (/ws), and every message is then judged by that token, including its expiry. App Check tokens last about an hour. On Enforce, a connection older than its token is closed at its next message, and the app reconnects with a fresh token. On WebSocket, a native user agent without a browser Origin counts as the app, for app builds that send no x-platform. This gate is always configured.",
		modes: {
			off: "Not checked.",
			log: "App connections and messages without a valid token are recorded and still served.",
			enforce:
				"A connection attempt with no token gets HTTP 403. A connection attempt with an invalid or expired token opens, then closes with code 4403. A message refused later gets CLIENT_PASS_REQUIRED, and the connection closes with 4403 one second later. Enforce this before Mobile App Check, once Mobile App Check itself is ready to enforce.",
		},
		outcome: {
			off: "App live-update connections are not checked.",
			log: "App connections and messages without a valid token are recorded and still served.",
			enforce:
				"App connections without a valid token are refused with HTTP 403 or closed with code 4403.",
		},
		missing: null,
	},
	rate_limit: {
		label: "Rate limits",
		help: "Call budgets per IP address, per signed-in user, per web pass, per App Check token and per internal pass, counted in Redis.",
		tooltip:
			"Counts every HTTP call and WebSocket message in fixed time windows against the budgets below. Each caller counts toward the budgets for its kind: signed-out callers per IP, signed-in callers per user, and web passes per pass. Server-rendered pages, internal passes and admins are counted only where a budget is set for them. If Redis is down or answers slower than 100 ms, calls go through.",
		modes: {
			off: "Nothing is counted, except Pass issuing, per IP, which the pass endpoint always applies.",
			log: "Calls are counted in the background without delay. Over-budget calls are recorded as would_block (reason over_limit). orpc.client_rate_count shows how high each count got, so you can size the budgets. Two extra counters measure every call and every search call per IP over 60 seconds, for the Cloudflare rules. They never block.",
			enforce:
				"A call over any budget gets 429 RATE_LIMITED with retryAfterSeconds. A WebSocket message gets the same error, and the connection stays open. Needs REDIS_URL. Enforce it right after the origin check and before the web pass, once real users never hit a budget over 7 days.",
		},
		outcome: {
			off: "Nothing is counted, except Pass issuing, per IP.",
			log: "Calls are counted, and over-budget calls are recorded and still served.",
			enforce: "Calls over a budget are refused with 429 RATE_LIMITED.",
		},
		missing: "REDIS_URL is missing",
	},
}

/** The rules that hold across gates. */
export const RULE_COPY = {
	notConfigured: {
		label: "Not configured",
		tooltip:
			"A gate that is not configured is skipped on every call and never blocks, whatever its mode. Those calls are recorded with reason not_configured, and Enforce can't be chosen or saved. Origin check needs CF_ORIGIN_AUTH_SECRET. Web pass and WebSocket, web need CLIENT_PASS_SIGNING_SECRET and a real TURNSTILE_SECRET_KEY (Cloudflare's test keys only count locally). Rate limits needs REDIS_URL. Mobile App Check and WebSocket, mobile are always configured, and browsers don't run the challenge for a web gate that is not configured.",
	},
	wsGatePairs: {
		tooltip:
			"The live-update connection (/ws) serves the same procedures as normal calls. Enforcing Web pass on its own would leave the same data open over a WebSocket. Saving is refused (WS_GATE_NOT_ENFORCED) whenever Web pass would be on Enforce without WebSocket, web, or Mobile App Check without WebSocket, mobile. This applies whether you raise the first gate or lower the second. To roll back, lower the WebSocket gate at the same time as its pass gate, or after it.",
	},
	propagation: {
		label: "When a change applies",
		description:
			"Every API server applies a saved change within about 30 seconds, with no deploy.",
		tooltip:
			"The server that handles the save applies it at once, and every other server rereads the settings every 30 seconds. If the database can't be read, a server keeps the last settings it loaded. A server that never loaded any runs every gate on Log. Browsers and the app reread the gate modes and challenge settings at most every 5 minutes, or right after a call is refused. Pass lifetime and binding apply only to passes issued after the save.",
	},
	enforceOrder: {
		label: "Order to enforce",
		description: "One gate at a time, never two on the same day.",
		tooltip:
			"1. Origin check. 2. Rate limits. 3. WebSocket, web, then Web pass. 4. WebSocket, mobile, then Mobile App Check, once min_app_version is raised in Firebase Remote Config. Watch errors and support for a day after each step. To undo a step, switch that gate back to Log. For a WebSocket gate whose pass gate is on Enforce, switch the pass gate back in the same save, or saving is refused. No deploy is needed.",
	},
} as const

/** Gates whose mode the website and the app also read, so their behaviour changes later than the servers'. */
export const CLIENT_READ_GATES: ReadonlySet<ClientCheckGate> =
	new Set<ClientCheckGate>(["web_pass", "ws_web", "app_check", "ws_mobile"])

/** When the website and the app pick up a change to a gate they read. */
export const CLIENTS_ADJUST_NOTE =
	"The website and the app adjust at their first call after their copy of these settings is 5 minutes old, or right after a call is refused."

/** Description of the Gates section, from the page intro. */
export const GATES_DESCRIPTION =
	"Each gate is Off, Log (measure only) or Enforce (refuse calls that fail). Every gate starts on Log, and a saved change reaches every API server within about 30 seconds without a deploy."

/** The settings of the Challenge section. */
export const VERIFIED_BOT_COPY = {
	trustedCategories: {
		label: "Trusted categories",
		help: "Bots Cloudflare verifies in these categories pass every gate without a client pass and never see the challenge.",
		tooltip:
			"Cloudflare sets the category on each request it recognises as a verified bot, and the server trusts it only when the request carries the origin header. Search Engine Crawler covers Googlebot and Bingbot. Anything checked here skips the pass gates and the Signed out budget, so check only the categories you want indexed or fetched freely. Calls from an unchecked category are handled like any other visitor.",
	},
} as const satisfies Record<string, FieldCopy>

export const CHALLENGE_COPY = {
	appearance: {
		label: "Challenge widget visibility",
		help: "How Cloudflare draws its widget inside the website's challenge dialog. Visitors see no difference today.",
		tooltip:
			"This value is passed to Cloudflare's Turnstile widget on the website. The widget sits inside the challenge dialog, which stays hidden until Cloudflare asks the visitor to click. When the dialog opens, the widget is visible with every option, so no visitor sees a difference whatever this is set to. Open pages keep the old value until they are reloaded. The app's challenge page always uses Only when needed. Anyone can read this setting.",
	},
	dialogInLog: {
		label: "Show the challenge while on Log",
		help: "Lets visitors complete a challenge that needs a click while the web gates are only on Log.",
		tooltip:
			"Sometimes Cloudflare wants a click before it hands out a token. On Enforce, the website always opens the challenge dialog for that. On Log, it opens the dialog only when this is on. When this is off, those visitors get no pass and their calls count as would_block, with no interruption. When it is on, they are interrupted, and the log data shows how many real visitors get through. Website only. Anyone can read this setting.",
	},
	allowedHostnames: {
		label: "Allowed hostnames",
		help: "Sites where a solved challenge can be swapped for a pass. One per line, up to 20.",
		tooltip:
			"Cloudflare reports the hostname each challenge was solved on. A solve from a hostname that is not on this list is refused (TURNSTILE_FAILED, reason hostname_mismatch), and the browser or app gets no pass. The website tries again 30 seconds later, and Dash0 counts the refusal under client_verification.pass_issuance. Passes already issued keep working. The hostname of the app challenge page must be on this list too. example.com matches exactly that hostname. *.example.com matches every subdomain at any depth but not example.com itself, and needs a dot after *., so *.com is refused. On dev, add *.<account>.workers.dev so PR previews can earn passes. The list in force is shown. Saving an edited list stores it for this environment, and it no longer follows changes to the code default. Empty the field to go back to the default.",
	},
	mobilePageUrl: {
		label: "App challenge page",
		help: "The page the app opens to solve the challenge when App Check fails.",
		tooltip:
			"When the app can't get an App Check token, it loads this page in a hidden web view. It solves Cloudflare's challenge there and gets a pass for its calls. The page's hostname must be in Allowed hostnames, or every solve from the app is refused. The app rereads this address at most every 5 minutes, and anyone can read it. Empty uses this environment's default: https://cardnexus.com/turnstile in production, https://dev.cardnexus.com/turnstile on staging and dev, and http://localhost:4200/turnstile locally. The address in force is shown. Saving an edited address stores it for this environment, and it no longer follows changes to the code default. Empty the field to go back to the default.",
	},
} as const satisfies Record<string, FieldCopy>

/** What an empty Allowed hostnames field means. */
export const DEFAULT_HOSTNAMES_NOTE =
	"Empty uses this environment's default list. Production: cardnexus.com, www.cardnexus.com and prod-cf.cardnexus.com. Staging: dev.cardnexus.com and dev-cf.cardnexus.com. Dev: the same plus localhost. Local: localhost."

/** The options of Challenge widget visibility, by Turnstile appearance value. */
export const APPEARANCE_OPTIONS: Record<TurnstileAppearance, OptionCopy> = {
	"interaction-only": {
		label: "Only when needed",
		effect:
			"Default. Cloudflare draws the widget only when it needs the visitor to click.",
	},
	always: {
		label: "Always visible",
		effect:
			"Cloudflare draws the widget for every check, but it stays inside the invisible challenge dialog, so visitors see no difference.",
	},
	execute: {
		label: "Once a check starts",
		effect:
			"Cloudflare draws the widget when a check starts. The website starts every check as soon as the widget loads, so this behaves like Always visible.",
	},
}

/** What each position of Show the challenge while on Log does. */
export const DIALOG_IN_LOG_OPTIONS = {
	on: {
		label: "Shown while on Log",
		effect:
			"On Log, visitors asked to click see the challenge dialog and can earn a pass.",
	},
	off: {
		label: "Hidden while on Log",
		effect:
			"Default. On Log, the challenge waits out of sight and the visitor carries on without a pass.",
	},
} as const satisfies Record<"on" | "off", OptionCopy>

/** The settings of the Client pass section. */
export const PASS_COPY = {
	ttlSeconds: {
		label: "Pass lifetime",
		help: "How long a web pass stays valid, in seconds (300 to 86,400).",
		tooltip:
			"Only passes issued after the save get the new lifetime. A longer lifetime means fewer challenges, but a copied pass keeps working longer, and Calls per pass is spread over more time. The website renews its pass during the last 2 minutes. With WebSocket, web on Enforce, a connection is closed with code 4403 at its first message after its pass expires. When you rotate CLIENT_PASS_SIGNING_SECRET, keep the old value for this long or 2 hours, whichever is longer: the same secret signs the AI matcher's Cardmarket image links, which stay valid for up to 2 hours. Default 1,800 (30 minutes).",
	},
	binding: {
		label: "Pass binding",
		help: "What a web pass is tied to, so it can't simply be copied to another client.",
		tooltip:
			"A pass presented with a different user agent, or from another network when the IP is bound, is refused as binding_mismatch. On Enforce, the website drops that pass and solves again. Only passes issued after the save use the new binding, and existing passes keep the binding they were issued with. Every binding_mismatch is logged, without the usual 10-second throttle. If Dash0 shows one pass id (orpc.client_pass_id) coming from many IPs, passes are being shared, and binding to the IP prefix stops that.",
	},
	perPass: {
		label: "Calls per pass",
		help: "How many calls one solved challenge buys over the whole pass lifetime. Default 2,000.",
		tooltip:
			"Counted per web pass (browsers and the app's challenge fallback) over the whole life of the pass, not per window. On Enforce, a pass that runs out gets 429 until it expires. The website renews a pass only in its last 2 minutes, so a very active visitor can be stuck for most of the lifetime. Leave it empty for no limit per pass.",
	},
} as const satisfies Record<string, FieldCopy>

/** The options of Pass binding, by binding value. */
export const BINDING_OPTIONS: Record<ClientPassBinding, OptionCopy> = {
	user_agent: {
		label: "Same browser",
		effect:
			"Default. The pass works only with the exact user agent it was issued to, so a browser update ends it.",
	},
	none: {
		label: "Nothing",
		effect:
			"Not tied to anything. A pass copied into a script works anywhere until it expires.",
	},
	user_agent_ip_prefix: {
		label: "Same browser and network",
		effect:
			"Tied to the user agent and the network: the first three numbers of an IPv4 address, or the first half of an IPv6 address. Visitors who switch networks, for example from Wi-Fi to mobile data, must solve again. The IPv6 prefix length setting does not apply here.",
	},
}

/** The settings of the Rate limits section that are not budgets. */
export const RATE_LIMIT_COPY = {
	budget: {
		label: "Calls per window",
		help: "Each budget is a number of calls per window of seconds. Leave it empty to turn it off.",
		tooltip:
			"Windows are fixed and aligned to the clock, so 300 per 60 seconds means at most 300 calls per clock minute. Changing only the limit applies to the count already in progress, while changing the window starts a new count. A window can be up to 86,400 seconds (one day). The prefilled defaults are provisional and were set before any log data existed. Size each budget from orpc.client_rate_count over 7 days.",
	},
	ipv6PrefixLength: {
		label: "IPv6 prefix length",
		help: "How many leading bits of an IPv6 address identify one caller for the per-IP budgets (32 to 128, default 64). Addresses that share those bits are counted together.",
		tooltip:
			"One IPv6 device can rotate through every address in its /64, so per-IP budgets group IPv6 callers by network prefix. A smaller number counts more addresses together, which is stricter but makes neighbours on a large network share one count. At 128, every address counts on its own, so a caller can get past the limit by switching addresses. IPv4 callers are always counted per single address, and this applies to every per-IP budget but not to pass binding.",
	},
} as const satisfies Record<string, FieldCopy>

/** Every calls-per-window budget. */
export const BUDGET_COPY: Record<BudgetKey, FieldCopy> = {
	anonymousPerIp: {
		label: "Signed out, per IP",
		help: "Calls without a signed-in user, per IP address. Default 300 per 60 seconds.",
		tooltip:
			"Counts calls without a signed-in session per IP address (IPv6 by prefix): browsers, the app and anything else, including calls to get a pass. Everyone behind one shared address, such as an office or a mobile carrier, shares this budget. Server-rendered pages, internal passes and admins are not counted here. On Enforce, a call over the budget gets 429 RATE_LIMITED until the window ends.",
	},
	signedInPerIp: {
		label: "Signed in, per IP",
		help: "Signed-in calls per IP address, on top of each user's own budget. Off by default.",
		tooltip:
			"Counts all signed-in calls from one IP address (IPv6 by prefix) together, to catch one address using many accounts. A shared office or carrier address counts all its signed-in users together, so set this well above a single user's budget.",
	},
	issuePassPerIp: {
		label: "Pass issuing, per IP",
		help: "How often one IP can swap a solved challenge for a pass. Default 30 per 600 seconds.",
		tooltip:
			"Counted per IP address (IPv6 by prefix) before Cloudflare is asked. It applies whatever the Rate limits gate is set to, even Off or Log. A browser or app over the budget gets 429 and no pass. The website then waits at least 30 seconds before trying again, and Dash0 counts client_verification.pass_issuance with outcome rate_limited. If Redis is down, nothing is limited.",
	},
	ssrPerVisitorIp: {
		label: "Server-rendered, per visitor",
		help: "Calls our server-rendered pages make for one visitor. Off by default.",
		tooltip:
			"Our website's server calls the API while it builds a page and signs the visitor's IP address into each call. This budget counts those calls per visitor IP (IPv6 by prefix). Calls made with no visitor, such as sitemaps and page rebuilds, are never counted, and server-rendered calls count toward no other budget. On Enforce, a page call over the budget gets 429, so keep this well above what one page view needs.",
	},
	verifiedBotPerIp: {
		label: "Verified bots, per IP",
		help: "Calls from trusted verified bots, per IP address. Off by default.",
		tooltip:
			"Counts the calls of the bot categories trusted above per IP address (IPv6 by prefix). Trusted bots are counted toward no other budget, so while this is empty they are never rate limited. On Enforce, a bot over the budget gets 429 RATE_LIMITED.",
	},
	userPerUser: {
		label: "Signed in, per user",
		help: "Calls of one signed-in user on every device. Default 600 per 60 seconds.",
		tooltip:
			"Counts the calls of one signed-in account across all its devices and networks. Signed-in calls don't count toward Signed out, per IP. Admins, server-rendered pages and internal passes are not counted here.",
	},
	adminPerUser: {
		label: "Admin, per user",
		help: "Calls of one admin. Off by default.",
		tooltip:
			"Counts calls from a signed-in user who holds admin roles, on the admin apps and the website alike. Admins count toward no other budget, so while this is empty they are never rate limited. On Enforce, an admin over the budget gets 429 everywhere, including this page, so keep this high.",
	},
	perAppCheckToken: {
		label: "Per App Check token",
		help: "Calls carrying the same App Check token. Off by default.",
		tooltip:
			"Counts the app's calls per App Check token in each window. The app refreshes its token in the background, and tokens last about an hour, so this caps what one copied token can do. The signed-in and signed-out budgets still apply alongside it.",
	},
	internalPerPass: {
		label: "Per internal pass",
		help: "Calls made with one internal pass. Off by default.",
		tooltip:
			'Scripts and tools with an internal pass are counted only here, so while this is empty they are never rate limited. On Enforce, a script over the budget gets 429 RATE_LIMITED with retryAfterSeconds. Dash0 traces calls per pass by the pass id (orpc.internal_pass_id), which the server log line "Internal pass created" records next to the pass name.',
	},
}

/** The budgets of the Rate limits section, grouped by what they count. */
export const BUDGET_GROUPS: readonly {
	title: string
	keys: readonly BudgetKey[]
}[] = [
	{
		title: "Per IP address",
		keys: [
			"anonymousPerIp",
			"signedInPerIp",
			"issuePassPerIp",
			"ssrPerVisitorIp",
			"verifiedBotPerIp",
		],
	},
	{ title: "Per signed-in user", keys: ["userPerUser", "adminPerUser"] },
	{
		title: "Per app token or internal pass",
		keys: ["perAppCheckToken", "internalPerPass"],
	},
]

/** Every budget key, in page order. */
export const BUDGET_KEYS: readonly BudgetKey[] = BUDGET_GROUPS.flatMap(
	(group) => group.keys,
)
